import React, { useState } from 'react';
import { Phone, Copy, Check, Facebook, Globe, Youtube } from 'lucide-react';

// Translation data
const translations = {
  th: {
    title: "เซฟติสท์ฟาร์ม",
    subtitle: "SAFETist Farm",
    facebook: "Facebook",
    contactRole: "ติดต่อ:",
    status: "สถานะ",
    line: "Line",
    copied: "คัดลอกแล้ว!",
    call: "โทร",
    contactHeading: "ข้อมูลติดต่อเจ้าหน้าที่",
    contactHeadingEn: "Contact Information"
  },
  en: {
    title: "SAFETist Farm",
    subtitle: "เซฟติสท์ฟาร์ม",
    facebook: "Facebook",
    contactRole: "Contact:",
    status: "Status",
    line: "Line",
    copied: "Copied!",
    call: "Call",
    contactHeading: "ข้อมูลติดต่อเจ้าหน้าที่",
    contactHeadingEn: "Contact Information"
  }
};

// Profile data with translations
const mockProfiles = [
  {
    id: 4,
    name: "ทัช",
    nameEn: "Touch",
    roles: ["ฝ่ายขนส่งตะกราผัก", "ฝ่ายกิจกรรมท่องเที่ยว"],
    rolesEn: ["Agriculture", "Tourism Activities"],
    avatar: "/ทัช.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0935015710",
    lineId: "tatsanajorn"
  },
  {
    id: 5,
    name: "ปรอ",
    nameEn: "Pror",
    roles: ["ฝ่ายกิจกรรมท่องเที่ยว"],
    rolesEn: ["Tourism Activities"],
    avatar: "/ปรอ.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0894818303",
    lineId: "wilaiwan.pror"
  },
  {
    id: 3,
    name: "สม",
    nameEn: "Som",
    roles: ["ฝ่ายดูแลการเดินทาง และจอดจอดรถ"],
    rolesEn: ["Transportation & Parking"],
    avatar: "/สม.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0844998075",
    lineId: "0844998075"
  },
  {
    id: 7,
    name: "หนุ่ย",
    nameEn: "Nui",
    roles: ["ฝ่ายดูแลการเดินทาง และจอดจอดรถ"],
    rolesEn: ["Transportation & Parking"],
    avatar: "/หนุ่ย.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0883850532",
    lineId: "0883850532"
  },
  {
    id: 6,
    name: "ไลล่า",
    nameEn: "Lila",
    roles: ["ผู้จัดการเซฟติสท์ฟาร์ม", "สมัครสมาชิกครอบครัวตะกร้าผัก"],
    rolesEn: ["SAFETist Farm Manager", "HR Manager"],
    avatar: "/ไลล่า.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0992866937",
    lineId: "0884984733"
  },
  {
    id: 1,
    name: "หญิง",
    nameEn: "Ying",
    roles: ["ฝ่ายกิจกรรมสำหรับเด็กและผู้ปกครอง"],
    rolesEn: ["Children & Parent Activities"],
    avatar: "/หญิง.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0841204983",
    lineId: "3809800"
  },
  {
    id: 2,
    name: "กิ๊ก",
    nameEn: "Gig",
    roles: ["ฝ่ายสื่อประชาสัมพันธ์"],
    rolesEn: ["Public Relations"],
    avatar: "/กิ๊ก.png",
    status: "Available",
    statusEn: "Available",
    phoneNumber: "0874434457",
    lineId: "vuditgmpq"
  }
];

// Language Toggle Component
const LanguageToggle = ({ lang, setLang }) => (
  <button
    onClick={() => setLang(lang === 'th' ? 'en' : 'th')}
    className="flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:border-[#E67E22] hover:bg-[#F9F7F2] transition-all duration-200 font-medium text-sm text-gray-700 hover:text-[#E67E22]"
  >
    <Globe size={16} />
    <span>{lang === 'th' ? 'English' : 'ไทย'}</span>
  </button>
);

// ProfileCard component
const ProfileCard = ({ name, nameEn, roles, rolesEn, status, statusEn, phoneNumber, lineId, avatar, lang }) => {
  const [copied, setCopied] = useState(false);
  const t = translations[lang];

  const handleCopyLineId = () => {
    if (lineId) {
      navigator.clipboard.writeText(lineId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="group bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col h-full border border-gray-100">
      {/* Status Badge */}
      <div className="absolute top-3 right-3 z-10">
        <span className="bg-[#5F8A58] text-white text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full">
          {statusEn}
        </span>
      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-6 flex flex-col items-center text-center flex-grow pt-6 sm:pt-8 w-full">
        {/* Avatar */}
        <div className="mb-3 sm:mb-5">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-gray-100">
            <img
              src={avatar}
              alt={name}
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Name and Roles */}
        <div className="mb-4 sm:mb-6">
          <h3 className="text-base sm:text-xl font-semibold text-[#2D2926] mb-1 sm:mb-2">
            {lang === 'th' ? name : `${name} (${nameEn})`}
          </h3>
          <div className="space-y-0.5 sm:space-y-1">
            {(lang === 'th' ? roles : rolesEn).map((role, index) => (
              <p key={index} className="text-[11px] sm:text-sm text-gray-500">
                {role}
              </p>
            ))}
          </div>
        </div>

        {/* Line ID Button */}
        {lineId && (
          <div className="w-full mb-4 sm:mb-6 flex justify-center">
            <button
              onClick={handleCopyLineId}
              className="inline-flex items-center justify-between bg-[#F9F7F2] hover:bg-[#E67E22] px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-all duration-200 group/btn"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="font-semibold text-[#E67E22] text-[10px] sm:text-xs uppercase bg-white px-1.5 sm:px-2 py-0.5 rounded flex-shrink-0 group-hover/btn:text-white group-hover/btn:bg-transparent transition-all">{t.line}</span>
                <span className="text-[11px] sm:text-sm text-gray-700 font-mono group-hover/btn:text-white transition-colors">{lineId}</span>
              </div>
              <div className="text-[#E67E22] group-hover/btn:text-white transition-colors flex-shrink-0 ml-2 sm:ml-3">
                {copied ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </div>
            </button>
          </div>
        )}

        {/* Divider */}
        <div className="w-10 sm:w-12 h-px bg-gray-200 mb-4 sm:mb-6"></div>
      </div>

      {/* Action Button (Footer) */}
      <div className="p-3 sm:p-4 pt-0 border-t border-gray-100 flex justify-center">
        <a
          href={`tel:${phoneNumber}`}
          className="inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-[#E67E22] hover:bg-[#5F8A58] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all duration-200 font-semibold text-[12px] sm:text-sm"
        >
          <Phone size={14} />
          <span>{phoneNumber}</span>
        </a>
      </div>
    </div>
  );
};


// Main App Component
const App = () => {
  const [lang, setLang] = useState('th');
  const t = translations[lang];

  return (
    <div className="min-h-screen bg-[#F9F7F2] font-sans">
      {/* Header */}
      <header className="bg-white sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            {/* Logo */}
            <img
              src="/logo-safetist.png"
              alt="SAFEtist Farm Logo"
              className="w-32 h-auto"
            />

            {/* Title */}
            <div className="text-center">
              <h1 className="text-2xl md:text-3xl font-bold text-[#5F8A58] mb-1">
                {t.title}
              </h1>
              <p className="text-base md:text-lg text-[#E67E22] font-medium mb-2">
                {t.subtitle}
              </p>
              <p className="text-sm md:text-base text-gray-600">
                แหล่งเรียนรู้นิเวศวิถีริมคลองบางมด
              </p>
            </div>

            {/* Language Toggle & Facebook */}
            <div className="flex items-center gap-3">
              <LanguageToggle lang={lang} setLang={setLang} />
              <a
                href="https://www.facebook.com/safetistfarm/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-[#5F8A58] hover:text-[#E67E22] hover:bg-[#F9F7F2] px-5 py-2.5 rounded-full transition-all duration-200 font-medium"
              >
                <Facebook size={18} />
                <span>{t.facebook}</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* YouTube Video Link Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10">
        <a
          href="https://youtu.be/VrsHBprRwbg?si=T0sUbPAeCToqI84-"
          target="_blank"
          rel="noopener noreferrer"
          className="group block bg-white border border-gray-200 hover:border-red-500 hover:shadow-lg transition-all duration-300 rounded-xl sm:rounded-2xl overflow-hidden"
        >
          <div className="relative aspect-video w-full">
            {/* Video Thumbnail */}
            <img
              src="https://img.youtube.com/vi/VrsHBprRwbg/hqdefault.jpg"
              alt="Video thumbnail"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            {/* Play Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-300">
              <div className="w-16 h-16 sm:w-20 md:w-24 sm:h-20 md:h-24 bg-red-500 rounded-full flex items-center justify-center shadow-lg group-hover:bg-red-600 transition-all duration-300 group-hover:scale-110">
                <Youtube className="text-white w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12" />
              </div>
            </div>
          </div>
          {/* Title */}
          <div className="p-4 sm:p-6 md:p-8 text-center">
            <p className="text-gray-700 group-hover:text-gray-900 font-bold text-base sm:text-lg md:text-xl transition-colors">
              {lang === 'th' ? 'วิดีโอการเดินทางมาเซฟติสท์ฟาร์ม' : 'Video: How to Get to SAFETist Farm'}
            </p>
          </div>
        </a>
      </div>

      {/* Grid of Profile Cards */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-10">
        {/* Contact Information Heading */}
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#5F8A58] mb-4 sm:mb-6 md:mb-8 text-center">
          {lang === 'th' ? t.contactHeading : t.contactHeadingEn}
        </h2>

        {/* All profiles in a single grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {mockProfiles.map(profile => (
            <ProfileCard
              key={profile.id}
              name={profile.name}
              nameEn={profile.nameEn}
              roles={profile.roles}
              rolesEn={profile.rolesEn}
              avatar={profile.avatar}
              status={profile.status}
              statusEn={profile.statusEn}
              phoneNumber={profile.phoneNumber}
              lineId={profile.lineId}
              lang={lang}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default App;
